'use client';

import type { LayerName } from '@slims/shared/energy';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { subscribeToTable } from '@/lib/supabase/realtime-registry';
import { api } from '@/lib/utils/api';
import { useAdminScopeStore } from '@/stores/admin-scope-store';

export interface MeterHourlyPoint {
	/** Inizio dell'ora (ISO 8601) */
	hour: string;
	/** kWh immessi nell'ora (M2 / produzione) */
	immessa: number;
	/** kWh prelevati nell'ora (M1 / consumo) */
	prelevata: number;
	source: LayerName;
}

export interface UseMeterHourlyParams {
	meterId?: string | null;
	from: string;
	to: string;
	/** Disattiva il fetch. */
	enabled?: boolean;
}

const EMPTY: MeterHourlyPoint[] = [];

/**
 * Curva di carico oraria di un singolo misuratore in [from, to].
 * Si aggiorna live: ogni nuova lettura su energy_readings (es. dal gateway)
 * innesca un refetch tramite la registry Realtime condivisa.
 *
 * ADR-006: backed by React Query — cache/dedup per misuratore e intervallo.
 */
export function useMeterHourly({ meterId, from, to, enabled = true }: UseMeterHourlyParams) {
	const queryClient = useQueryClient();
	// Meter curves live in the admin area: scope to the admin configuration.
	const orgId = useAdminScopeStore((s) => s.configId);
	const active = !!meterId && enabled && !!orgId;

	const {
		data,
		isLoading,
		error,
		refetch: rqRefetch,
	} = useQuery({
		queryKey: ['meter-hourly', orgId, meterId, from, to],
		enabled: active,
		refetchInterval: 60_000,
		queryFn: () => {
			const params = new URLSearchParams({
				meterId: meterId as string,
				from,
				to,
				orgId: orgId as string,
			});
			return api.get<MeterHourlyPoint[]>(`/api/energy/historical?${params.toString()}`);
		},
	});

	const refetch = useCallback(async () => {
		await rqRefetch();
	}, [rqRefetch]);

	// La riconciliazione sceglie automaticamente GSE, reale, stimato, tipico.
	// I nuovi dati invalidano la curva; il polling copre anche le proiezioni
	// generate in background, che non pubblicano eventi Realtime.
	useEffect(() => {
		if (!meterId || !enabled) return;
		let timer: ReturnType<typeof setTimeout> | null = null;
		const onChange = () => {
			if (timer) clearTimeout(timer);
			timer = setTimeout(() => {
				queryClient.invalidateQueries({ queryKey: ['meter-hourly', orgId, meterId] });
			}, 800);
		};
		const unsubReadings = subscribeToTable(
			`energy-readings-realtime-${meterId}`,
			{ event: '*', schema: 'public', table: 'energy_readings', filter: `meter_id=eq.${meterId}` },
			onChange,
		);
		const unsubEstimated = subscribeToTable(
			`estimated-data-realtime-${meterId}`,
			{ event: '*', schema: 'public', table: 'estimated_data', filter: `meter_id=eq.${meterId}` },
			onChange,
		);
		return () => {
			if (timer) clearTimeout(timer);
			unsubReadings();
			unsubEstimated();
		};
	}, [meterId, enabled, orgId, queryClient]);

	return {
		data: Array.isArray(data) ? data : EMPTY,
		isLoading: active ? isLoading : false,
		error: error
			? error instanceof Error
				? error.message
				: 'Errore nel caricamento curva di carico'
			: null,
		refetch,
	};
}
